import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/services' },
  { name: 'About', href: '/about' },
  { name: 'Impact', href: '/impact' },
  { name: 'Insights', href: '/insights' },
  { name: 'Contact', href: '/contact' },
];

export function Header() {
  // Initialize with null to indicate "not yet determined" - prevents hydration mismatch
  const [isScrolled, setIsScrolled] = useState<boolean | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    setIsMounted(true);
    // Set initial scroll state on mount
    setIsScrolled(window.scrollY > 20);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // On homepage, header is transparent until scrolled
  // During SSR and initial hydration (isScrolled === null), use a safe default
  // that matches what the server renders
  const showWhiteBg = isScrolled === null ? !isHomePage : (isScrolled || !isHomePage);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showWhiteBg
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-eco-green-900/5'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo - WebP with PNG fallback */}
          <Link to="/" className="flex items-center gap-3 group">
            <picture>
              <source srcSet="/images/logo.webp" type="image/webp" />
              <img
                src="/images/logo.png"
                alt="Eco Enviro"
                width={160}
                height={64}
                className={`h-14 lg:h-16 w-auto object-contain transition-transform hover:scale-105 ${!showWhiteBg ? 'brightness-0 invert' : ''}`}
                loading="eager"
              />
            </picture>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative"
              >
                <Link
                  to={item.href}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                    location.pathname === item.href
                      ? showWhiteBg 
                        ? 'text-eco-green-700 bg-eco-green-50'
                        : 'text-white bg-white/20'
                      : showWhiteBg
                        ? 'text-gray-700 hover:text-eco-green-700 hover:bg-eco-green-50/50'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.name}
                </Link>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact?quote=true"
              className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 shadow-lg hover:scale-[1.02] active:scale-[0.98] ${
                showWhiteBg
                  ? 'text-white bg-gradient-to-r from-eco-green-600 to-eco-green-700 hover:from-eco-green-700 hover:to-eco-green-800 shadow-eco-green-600/30'
                  : 'text-eco-green-800 bg-white hover:bg-eco-green-50 shadow-black/20'
              }`}
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            className={`lg:hidden p-2.5 rounded-xl transition-colors active:scale-90 ${
              showWhiteBg ? 'hover:bg-eco-green-50' : 'hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${showWhiteBg ? 'text-eco-green-700' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${showWhiteBg ? 'text-eco-green-700' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu - CSS animations to match framer-motion behavior */}
        {isMounted && (
          <div
            className={`lg:hidden overflow-hidden transition-all duration-300 ease-out ${
              isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-4 space-y-1 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl mb-4 border border-eco-green-100/80">
              {navigation.map((item, index) => (
                <div
                  key={item.name}
                  className={`transition-all duration-300 ease-out ${
                    isMobileMenuOpen 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 -translate-x-5'
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? `${index * 50}ms` : '0ms' }}
                >
                  <Link
                    to={item.href}
                    className="block px-5 py-3 text-base font-medium text-gray-700 hover:text-eco-green-700 hover:bg-eco-green-50 rounded-xl mx-2"
                  >
                    {item.name}
                  </Link>
                </div>
              ))}
              <div className="pt-4 px-4 space-y-2 border-t border-eco-green-100 mt-4">
                <Link
                  to="/contact?quote=true"
                  className="block w-full px-5 py-3 text-center text-sm font-semibold text-white bg-gradient-to-r from-eco-green-600 to-eco-green-700 rounded-xl"
                >
                  Get Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
